<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ImportBankStatement',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const file = ref(null);
  const formId = ref(null);
  const formName = ref('ImportBankStatement');
  const resultText = ref(null);
  const bankStatementFormats = ref([
    {
      value: 'unicredit',
      text: $fn.value.pgettext('BankStatementFormat', 'UniCredit XLSX'),
      accept: '.xlsx',
    },
    {
      value: 'cib',
      text: $fn.value.pgettext('BankStatementFormat', 'CIB csv'),
      accept: '.csv',
    },
    {
      value: 'txt',
      text: $fn.value.pgettext('BankStatementFormat', 'Text (TXT)'),
      accept: '.txt,.stm',
    },
  ]);
  const format = ref(null);
  const transaction_number = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_2359849487788347_validation = computed(() => ['required']);

  const $data = computed(() => ({
    file: file.value,
    formId: formId.value,
    formName: formName.value,
    resultText: resultText.value,
    bankStatementFormats: bankStatementFormats.value,
    format: format.value,
    transaction_number: transaction_number.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2359849487788347_validation: b_2359849487788347_validation.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_format = async () => {
    return bankStatementFormats.value[0].value;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'file' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'resultText' },
      { name: 'bankStatementFormats' },
      {
        name: 'format',
        cache: { type: 'local', key: 'ImportBankStatement.format' },
      },
      { name: 'transaction_number' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2359849487788347_validation', nodeId: '2359849487788347' },
    ],
    loaders: { formId: $$load_formId, format: $$load_format },
    ux: {},
    dataRefs: {
      file,
      formId,
      formName,
      resultText,
      bankStatementFormats,
      format,
      transaction_number,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2359849487788347_validation,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const currentFormat = computed(() => {
    if (loaded.value) {
      return _.find(bankStatementFormats.value, { value: format.value });
    }
    return null;
  });
  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'ImportBankStatement|Title',
        'Import Bank Statement'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_mdhi8hh9e_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_648747096704084_setData_i8vkyjbz3_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'resultText',
      value: $fn.value.pgettext('ImportBankStatement', 'Successful import.'),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_648747096704084_request_9c6u791rc_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_payment_from_statement',
      data: {
        file: file.value,
        format: format.value,
        transaction_number: transaction_number.value,
      },
      contentType: 'multipart/form-data',
    };
  };
  const act_2434514588044777_closeModal_iineendp8_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_mdhi8hh9e =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_mdhi8hh9e_getActionArgs,
    });
  const inst_evh_7315092382398562_close_681r5hi8t =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_mdhi8hh9e,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_681r5hi8t],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_31u515ycf =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_x6zomkpbx =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_31u515ycf,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_x6zomkpbx],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_dnw1165oe =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_etqc30h8j =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_dnw1165oe,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_etqc30h8j],
    event: 'close',
  });
  const act_648747096704084_setData_i8vkyjbz3 = new actionTypes.SetDataAction({
    actionArgs: act_648747096704084_setData_i8vkyjbz3_getActionArgs,
  });
  const inst_evh_648747096704084_success_5vpcdpnpw =
    new actionTypes.EventHandlerImpl({
      action: act_648747096704084_setData_i8vkyjbz3,
      event: 'success',
      displayName: 'setData',
    });
  const act_648747096704084_request_9c6u791rc = new actionTypes.RequestAction({
    actionArgs: act_648747096704084_request_9c6u791rc_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_648747096704084_success_5vpcdpnpw],
  });
  const inst_evh_648747096704084_submit_sc0n51gjt =
    new actionTypes.EventHandlerImpl({
      action: act_648747096704084_request_9c6u791rc,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_648747096704084_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_648747096704084_submit_sc0n51gjt],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_iineendp8 =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_iineendp8_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_pvx6ffqy2 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_iineendp8,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_pvx6ffqy2],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_681r5hi8t,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_x6zomkpbx,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_etqc30h8j,
    inst_evh_8564662037462133_close,
    inst_evh_648747096704084_success_5vpcdpnpw,
    inst_evh_648747096704084_submit_sc0n51gjt,
    submit: inst_evh_648747096704084_submit_sc0n51gjt,
    inst_evh_648747096704084_submit,
    inst_evh_2434514588044777_clickPreventStop_pvx6ffqy2,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_681r5hi8t = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_681r5hi8t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_x6zomkpbx = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_x6zomkpbx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_etqc30h8j = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_etqc30h8j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_success_5vpcdpnpw = async (event, scope) => {
    return await actions.inst_evh_648747096704084_success_5vpcdpnpw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_submit_sc0n51gjt = async (event, scope) => {
    return await actions.inst_evh_648747096704084_submit_sc0n51gjt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_submit = async (event, scope) => {
    return await actions.inst_evh_648747096704084_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_pvx6ffqy2 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_pvx6ffqy2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ImportBankStatement"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpAlert variant="info" show>
        <ItpIcon fa="info-circle"> </ItpIcon>
        <ItpText wrap>
          A funkció a banki alkalmazásból letöltött állományokból importál banki
          bizonylatokat.
        </ItpText>
      </ItpAlert>
      <hr />
      <div class="container">
        <ItpForm
          :name="formName"
          :id="formId"
          v-if="loaded"
          @submit="evh_648747096704084_submit($event, {})"
        >
          <ItpFormGroup
            name="format"
            :label="pgettext('ImportBankStatement', 'Statement format')"
            labelFor="format"
            required
          >
            <ItpFormSelect2
              v-model="format"
              :value="format"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (format =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              name="format"
              required
              :options="bankStatementFormats"
            >
            </ItpFormSelect2>
          </ItpFormGroup>
          <ItpFormGroup
            name="transaction_number"
            :label="pgettext('ImportBankStatement', 'Payment number')"
            labelFor="transaction_number"
          >
            <ItpFormInput
              v-model="transaction_number"
              :value="transaction_number"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (transaction_number =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              name="transaction_number"
            >
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            name="file"
            :label="pgettext('ImportBankStatement', 'Statement file')"
            labelFor="file"
            :description="
              pgettext(
                'ImportBankStatement',
                'The file must be downloaded from the respective bank\'s application.'
              )
            "
            required
          >
            <ItpFormFile
              v-model="file"
              :value="file"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (file =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              name="file"
              :accept="currentFormat && currentFormat.accept"
              required
              :validation="b_2359849487788347_validation"
            >
            </ItpFormFile>
          </ItpFormGroup>
        </ItpForm>
        <ItpAlert :show="resultText && !actions.submit.isRunning">
          <p>
            {{ resultText }}
          </p>
        </ItpAlert>
      </div>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="formId"
          :text="pgettext('ImportBankStatement', 'Submit file')"
          type="submit"
          variant="primary"
          icon="fa-file-import"
          :spinning="actions.submit.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
